import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { StatusBadge } from '@/components/common/StatusBadge';
import DataTable from '@/components/common/DataTable';
import { userAPI, agencyAPI, authAPI } from '@/lib/api';
import { isKRRole, formatDate, getErrorMessage } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Users, UserCog, Shield, Plus, Edit, Trash2, Loader2, Power,
  PowerOff, ShieldPlus, Mail, Building2, X, KeyRound, Copy
} from 'lucide-react';

const ROLE_OPTIONS = [
  { value: 'KR_SuperAdmin', label: 'KR Super Admin' },
  { value: 'KR_AdminOps', label: 'KR Admin Ops' },
  { value: 'Accounting', label: 'Accounting' },
  { value: 'Agency_Principal', label: 'Agency Principal' },
  { value: 'Agency_User', label: 'Agency User' },
  { value: 'Agent', label: 'Agent' },
];

const ROLE_COLORS = {
  KR_SuperAdmin: 'bg-red-100 text-red-700',
  KR_AdminOps: 'bg-orange-100 text-orange-700',
  Accounting: 'bg-purple-100 text-purple-700',
  Agency_Principal: 'bg-blue-100 text-blue-700',
  Agency_User: 'bg-sky-100 text-sky-700',
  Agent: 'bg-emerald-100 text-emerald-700',
};

export default function UsersPage() {
  const { user } = useAuth();
  const isKR = isKRRole(user);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [filterValues, setFilterValues] = useState({});

  // Dialogs
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showAddRoleDialog, setShowAddRoleDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '' });
  const [newRole, setNewRole] = useState({ role: '', agency_id: '' });

  // Password reset state
  const [resetTarget, setResetTarget] = useState(null);            // user being reset
  const [resetMode, setResetMode] = useState('email');             // 'email' | 'temp'
  const [resetNotes, setResetNotes] = useState('');
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetResult, setResetResult] = useState(null);             // {temp_password?, sent?, email}

  const openResetDialog = (row) => {
    setResetTarget(row);
    setResetMode('email');
    setResetNotes('');
    setResetResult(null);
  };
  const closeResetDialog = () => {
    setResetTarget(null); setResetResult(null); setResetNotes('');
  };
  const submitReset = async () => {
    if (!resetTarget) return;
    setResetSubmitting(true);
    try {
      if (resetMode === 'email') {
        const r = await authAPI.adminSendResetEmail(resetTarget.user_id, resetNotes || undefined);
        setResetResult({ sent: r.data.sent, email: r.data.email, mode: 'email' });
        toast.success(r.data.sent ? `Reset email sent to ${r.data.email}` : `Could not send email — check SendGrid config`);
      } else {
        const r = await authAPI.adminResetTemp(resetTarget.user_id, resetNotes || undefined);
        setResetResult({ temp_password: r.data.temp_password, email: r.data.email, mode: 'temp' });
        toast.success(`Temporary password generated for ${r.data.email}`);
      }
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setResetSubmitting(false);
    }
  };
  const copyTempPwd = () => {
    if (resetResult?.temp_password) {
      navigator.clipboard.writeText(resetResult.temp_password);
      toast.success('Copied to clipboard');
    }
  };

  const load = useCallback(async () => {
    try {
      const [usersRes, agenciesRes] = await Promise.all([
        userAPI.list(), agencyAPI.list()
      ]);
      setUsers(usersRes.data);
      setAgencies(agenciesRes.data);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Helper to get agency name
  const getAgencyName = useCallback((agencyId) => {
    const a = agencies.find(ag => ag.agency_id === agencyId);
    return a?.agency_name || agencyId || '';
  }, [agencies]);

  // Filter definitions
  const filterDefs = useMemo(() => {
    const roleOpts = [...new Set(users.flatMap(u => (u.roles || []).map(r => r.role)))].sort().map(r => ({
      value: r, label: ROLE_OPTIONS.find(o => o.value === r)?.label || r
    }));
    return [
      { key: 'status', label: 'Status', options: [
        { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }
      ]},
      { key: 'role', label: 'Role', options: roleOpts },
    ];
  }, [users]);

  const filterFn = useCallback((row, fv) => {
    if (fv.status && fv.status !== 'all' && row.status !== fv.status) return false;
    if (fv.role && fv.role !== 'all') {
      if (!(row.roles || []).some(r => r.role === fv.role)) return false;
    }
    return true;
  }, []);

  // Actions
  const openDetail = (u) => {
    setSelectedUser(u);
    setShowDetailDialog(true);
  };

  const openEdit = (u) => {
    setSelectedUser(u);
    setEditForm({ name: u.name || '', email: u.email || '' });
    setShowEditDialog(true);
  };

  const openAddRole = (u) => {
    setSelectedUser(u);
    setNewRole({ role: '', agency_id: '' });
    setShowAddRoleDialog(true);
  };

  const handleToggleStatus = async (u) => {
    const newStatus = u.status === 'active' ? 'inactive' : 'active';
    try {
      await userAPI.updateStatus(u.user_id, newStatus);
      toast.success(`User ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const handleEditSave = async () => {
    try {
      await userAPI.update(selectedUser.user_id, editForm);
      toast.success('User updated');
      setShowEditDialog(false);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const handleAddRole = async () => {
    if (!newRole.role) { toast.error('Select a role'); return; }
    const needsAgency = ['Agency_Principal', 'Agency_User', 'Agent'].includes(newRole.role);
    if (needsAgency && !newRole.agency_id) { toast.error('Select an agency'); return; }
    try {
      await userAPI.assignRole({
        user_id: selectedUser.user_id,
        role: newRole.role,
        agency_id: needsAgency ? newRole.agency_id : '',
      });
      toast.success('Role added');
      setShowAddRoleDialog(false);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const handleRemoveRole = async (u, roleId) => {
    try {
      await userAPI.removeRole(u.user_id, roleId);
      toast.success('Role removed');
      load();
      // Refresh detail if open
      if (selectedUser?.user_id === u.user_id) {
        const updated = await userAPI.list();
        const refreshed = updated.data.find(x => x.user_id === u.user_id);
        if (refreshed) setSelectedUser(refreshed);
      }
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const handleDelete = async () => {
    try {
      await userAPI.delete(deleteTarget.user_id);
      toast.success('User deleted');
      setDeleteTarget(null);
      setShowDetailDialog(false);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  if (!isKR) return <div className="p-8 text-center text-slate-500">Access denied.</div>;
  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin" size={32} /></div>;

  const columns = [
    {
      header: 'User', accessor: 'name', sortable: true,
      cell: (row) => (
        <div>
          <p className="text-sm font-medium">{row.name}</p>
          <p className="text-xs text-slate-500">{row.email}</p>
        </div>
      )
    },
    {
      header: 'Roles', accessor: 'roles',
      cell: (row) => (
        <div className="flex flex-wrap gap-1">
          {(row.roles || []).map((r, i) => (
            <Badge key={i} variant="outline" className={`text-[10px] ${ROLE_COLORS[r.role] || ''}`}>
              {ROLE_OPTIONS.find(o => o.value === r.role)?.label || r.role}
              {r.agency_id && <span className="ml-0.5 opacity-60">({getAgencyName(r.agency_id).substring(0, 15)})</span>}
            </Badge>
          ))}
          {(!row.roles || row.roles.length === 0) && (
            <span className="text-xs text-slate-400 italic">No roles</span>
          )}
        </div>
      )
    },
    {
      header: 'Status', accessor: 'status', sortable: true,
      cell: (row) => <StatusBadge status={row.status} />
    },
    {
      header: 'Created', accessor: 'created_at', sortable: true,
      cell: (row) => <span className="text-xs text-slate-500">{formatDate(row.created_at)}</span>
    },
    {
      header: 'Actions',
      cell: (row) => (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => openDetail(row)} data-testid={`view-user-${row.user_id}`} title="View Details">
            <UserCog size={14} />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => openAddRole(row)} data-testid={`add-role-${row.user_id}`} title="Add Role">
            <ShieldPlus size={14} />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleToggleStatus(row)}
            data-testid={`toggle-status-${row.user_id}`}
            title={row.status === 'active' ? 'Deactivate' : 'Activate'}
            disabled={row.user_id === user.user_id}>
            {row.status === 'active' ? <PowerOff size={14} className="text-amber-600" /> : <Power size={14} className="text-emerald-600" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => openResetDialog(row)} data-testid={`reset-pwd-${row.user_id}`} title="Reset Password">
            <KeyRound size={14} className="text-sky-600" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => openEdit(row)} data-testid={`edit-user-${row.user_id}`} title="Edit">
            <Edit size={14} />
          </Button>
          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => setDeleteTarget(row)}
            data-testid={`delete-user-${row.user_id}`} title="Delete"
            disabled={row.user_id === user.user_id}>
            <Trash2 size={14} />
          </Button>
        </div>
      )
    },
  ];

  return (
    <div data-testid="users-page">
      <PageHeader
        title="User Management"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Users', href: '/users' },
        ]}
      />

      <div className="page-content">
        <DataTable
          columns={columns}
          data={users}
          emptyTitle="No users"
          emptyDescription="No users found in the system."
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          searchPlaceholder="Search by name or email..."
          searchFields={['name', 'email']}
          filters={filterDefs}
          filterValues={filterValues}
          onFilterChange={(key, val) => setFilterValues(prev => ({...prev, [key]: val}))}
          filterFn={filterFn}
          defaultSortKey="name"
          defaultSortDir="asc"
          defaultPageSize={25}
        />
      </div>

      {/* User Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          {selectedUser && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  <Shield size={18} /> {selectedUser.name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-500">Email:</span> <strong>{selectedUser.email}</strong></div>
                  <div><span className="text-slate-500">Status:</span> <StatusBadge status={selectedUser.status} /></div>
                  <div><span className="text-slate-500">Created:</span> {formatDate(selectedUser.created_at)}</div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold">Roles ({(selectedUser.roles || []).length})</h4>
                    <Button size="sm" variant="outline" onClick={() => { setShowDetailDialog(false); openAddRole(selectedUser); }}>
                      <Plus size={12} className="mr-1" /> Add Role
                    </Button>
                  </div>
                  {(selectedUser.roles || []).length === 0 ? (
                    <p className="text-sm text-slate-400 italic">No roles assigned</p>
                  ) : (
                    <div className="space-y-2">
                      {(selectedUser.roles || []).map((role, i) => (
                        <div key={role.id || i} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                          <div>
                            <Badge className={`${ROLE_COLORS[role.role] || ''} text-xs`}>
                              {ROLE_OPTIONS.find(o => o.value === role.role)?.label || role.role}
                            </Badge>
                            {role.agency_id && (
                              <span className="text-xs text-slate-500 ml-2">
                                <Building2 size={10} className="inline mr-0.5" />
                                {getAgencyName(role.agency_id)}
                              </span>
                            )}
                          </div>
                          <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600 h-6 w-6 p-0"
                            onClick={() => handleRemoveRole(selectedUser, role.id)}
                            data-testid={`remove-role-${role.id}`}>
                            <X size={12} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => { setShowDetailDialog(false); openEdit(selectedUser); }}>
                  <Edit size={14} className="mr-1" /> Edit
                </Button>
                <Button variant="destructive" size="sm" onClick={() => { setShowDetailDialog(false); setDeleteTarget(selectedUser); }}
                  disabled={selectedUser.user_id === user.user_id}>
                  <Trash2 size={14} className="mr-1" /> Delete
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Role Dialog */}
      <Dialog open={showAddRoleDialog} onOpenChange={setShowAddRoleDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Role to {selectedUser?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Role *</Label>
              <Select value={newRole.role} onValueChange={v => setNewRole(p => ({ ...p, role: v }))}>
                <SelectTrigger data-testid="new-role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {['Agency_Principal', 'Agency_User', 'Agent'].includes(newRole.role) && (
              <div className="space-y-2">
                <Label>Agency *</Label>
                <Select value={newRole.agency_id} onValueChange={v => setNewRole(p => ({ ...p, agency_id: v }))}>
                  <SelectTrigger data-testid="new-role-agency-select"><SelectValue placeholder="Select agency" /></SelectTrigger>
                  <SelectContent>
                    {agencies.map(a => <SelectItem key={a.agency_id} value={a.agency_id}>{a.agency_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600">
              <p className="font-medium mb-1">Current roles:</p>
              {(selectedUser?.roles || []).map((r, i) => (
                <p key={i}>
                  {ROLE_OPTIONS.find(o => o.value === r.role)?.label || r.role}
                  {r.agency_id ? ` — ${getAgencyName(r.agency_id)}` : ''}
                </p>
              ))}
              {(selectedUser?.roles || []).length === 0 && <p className="italic text-slate-400">None</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddRoleDialog(false)}>Cancel</Button>
            <Button onClick={handleAddRole} data-testid="confirm-add-role-btn">Add Role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} data-testid="edit-user-name" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={editForm.email} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} data-testid="edit-user-email" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleEditSave} data-testid="save-edit-user-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the user account for <strong>{deleteTarget?.name}</strong> ({deleteTarget?.email}) and remove all their roles. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-user">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(v) => !v && closeResetDialog()}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset Password — {resetTarget?.name || resetTarget?.email}</DialogTitle>
          </DialogHeader>
          {!resetResult ? (
            <div className="space-y-4">
              <div className="text-sm text-slate-600">Choose how to reset this user&apos;s password:</div>
              <div className="space-y-2">
                <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition ${resetMode === 'email' ? 'border-sky-500 bg-sky-50' : 'border-slate-200'}`}>
                  <input type="radio" checked={resetMode === 'email'} onChange={() => setResetMode('email')} data-testid="reset-mode-email" />
                  <div>
                    <div className="font-medium">Send reset email</div>
                    <div className="text-xs text-slate-500">User receives a secure link valid for 24 hours. Recommended.</div>
                  </div>
                </label>
                <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition ${resetMode === 'temp' ? 'border-amber-500 bg-amber-50' : 'border-slate-200'}`}>
                  <input type="radio" checked={resetMode === 'temp'} onChange={() => setResetMode('temp')} data-testid="reset-mode-temp" />
                  <div>
                    <div className="font-medium">Generate temporary password</div>
                    <div className="text-xs text-slate-500">Shown once. User must change it on next login.</div>
                  </div>
                </label>
              </div>
              <div>
                <Label>Reason / Notes (optional)</Label>
                <Input value={resetNotes} onChange={(e) => setResetNotes(e.target.value)}
                       placeholder="e.g. user reported lost access" data-testid="reset-notes" />
              </div>
            </div>
          ) : resetResult.mode === 'email' ? (
            <div className="space-y-3">
              {resetResult.sent ? (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                  Reset email sent to <b>{resetResult.email}</b>. The link expires in 24 hours.
                </div>
              ) : (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  Could not send email (SendGrid returned an error). The reset token was still created — check the user record / audit log, or try Temporary Password instead.
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                <div className="font-medium mb-1">Share this with <b>{resetResult.email}</b> via a secure channel.</div>
                <div className="text-xs">It will <b>not</b> be shown again. User will be forced to change it on next login.</div>
              </div>
              <div className="flex items-center gap-2 bg-slate-900 text-emerald-200 font-mono p-3 rounded">
                <code className="flex-1 break-all text-sm" data-testid="reset-temp-pwd-value">{resetResult.temp_password}</code>
                <Button size="sm" variant="ghost" className="text-emerald-200 hover:bg-slate-700" onClick={copyTempPwd} data-testid="reset-copy-btn">
                  <Copy size={14} className="mr-1" />Copy
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            {!resetResult ? (
              <>
                <Button variant="outline" onClick={closeResetDialog}>Cancel</Button>
                <Button onClick={submitReset} disabled={resetSubmitting} data-testid="reset-submit-btn">
                  {resetSubmitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                  {resetMode === 'email' ? 'Send Reset Email' : 'Generate Temp Password'}
                </Button>
              </>
            ) : (
              <Button onClick={closeResetDialog} data-testid="reset-close-btn">Close</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
